// src/setSource.js
//
// Unified source of truth for set metadata.
// Merges Supabase `sets` table (Admin-managed, source of truth going forward)
// with the hardcoded SET_REGISTRY JS file (source of `dataFile` pointers for
// card-level data).
//
// Supabase wins for display metadata (name, description, color, etc.).
// JS wins for dataFile (so existing card data keeps loading).
// Fallback to JS if Supabase fetch fails or returns empty.
//
// Used by: SetBrowser.js (and future readers).

import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';

// Transform a Supabase row (snake_case) into the shape the rest of the app
// expects (camelCase, with `id` = slug).
function normalizeSupabaseSet(row) {
  return {
    id: row.slug,
    slug: row.slug,
    name: row.name,
    shortName: row.short_name || row.name,
    year: row.year,
    brand: row.manufacturer,
    category: row.category,
    releaseDate: row.release_date,
    totalCards: row.total_cards,
    description: row.description,
    color: row.color,
    accentColor: row.accent_color,
    dataFile: row.data_file,
    section: row.section,
    publishedAt: row.published_at,
    isPublished: row.is_published,
  };
}

// Merge a Supabase set with its JS counterpart (if one exists).
// Supabase metadata wins; JS dataFile is preserved if Supabase doesn't have one.
function mergeWithJs(supaSet, jsSet) {
  if (!jsSet) return supaSet;
  return {
    ...jsSet,              // baseline from JS (has dataFile and fallback metadata)
    ...supaSet,            // Supabase overrides for metadata (wins for all populated fields)
    dataFile: supaSet.dataFile || jsSet.dataFile, // prefer Supabase, fall back to JS
  };
}

// Load the merged, public-facing set list.
// Returns an array of set objects in the shape existing rendering code expects.
export async function loadPublishedSets() {
  try {
    const { data, error } = await supabase
      .from('sets')
      .select('slug, name, short_name, year, manufacturer, category, release_date, total_cards, description, color, accent_color, data_file, section, published_at, is_published')
      .eq('is_published', true)
      .eq('is_archived', false);

    if (error) {
      console.warn('[setSource] Supabase fetch failed, falling back to JS registry:', error.message);
      return SET_REGISTRY;
    }

    if (!data || data.length === 0) {
      // Nothing published in Supabase yet. Fall back to JS so users still see their sets.
      return SET_REGISTRY;
    }

    // Build a map of JS sets by slug for merge lookup.
    const jsMap = {};
    for (const s of SET_REGISTRY) jsMap[s.id] = s;

    // For each published Supabase set, merge with JS (if slug matches) or use Supabase as-is.
    const mergedFromSupabase = data.map(row => {
      const supaSet = normalizeSupabaseSet(row);
      return mergeWithJs(supaSet, jsMap[supaSet.slug]);
    });

    // Include JS sets that don't have a Supabase entry at all (safety net —
    // shouldn't happen since we migrated everything, but prevents a JS set
    // from silently disappearing if someone forgot to publish it in Supabase).
    const supaSlugs = new Set(data.map(row => row.slug));
    const jsOnlySets = SET_REGISTRY.filter(s => !supaSlugs.has(s.id));

    return [...mergedFromSupabase, ...jsOnlySets];
  } catch (e) {
    console.warn('[setSource] Unexpected error, falling back to JS registry:', e);
    return SET_REGISTRY;
  }
}
